/**
 * Formats a given amount in cents to a currency string.
 *
 * @param cents - The amount in cents to format. Defaults to 0.
 * @param currency - The currency code to use for formatting. Defaults to 'CHF'.
 * @param negative - Indicates if the amount should be formatted as negative. Defaults to false.
 * @param rounded - Indicates if the amount should be rounded. Defaults to false.
 * @param dash - Indicates if .00 should be replaced with a dash. Defaults to false.
 * @returns The formatted currency string.
 */
export function formatCents(cents: number = 0, currency: string = 'CHF', negative: boolean = false, rounded = false, dash = false): string {
    // Use 'de-CH' as default number formatter, except for English
    const numberFormatLocale = useNuxtApp().$i18n.locale.value === 'en' ? 'en' : 'de-CH';

    let val = cents * (negative ? -1 : 1) / 100;
    if (rounded) {
        val = Math.round(val);
    }

    let formattedPrice = new Intl.NumberFormat(numberFormatLocale, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: rounded ? 0 : 2,
        maximumFractionDigits: rounded ? 0 : 2,
    }).format(val);

    // Add space between currency and minus sign if necessary
    if (negative && formattedPrice.includes('-')) {
        formattedPrice = formattedPrice.replace(/(\D)-(\d)/, '$1 -$2');
    }

    if (dash) {
        formattedPrice = formattedPrice.replace('.00', '.—');
    }

    return formattedPrice;
}

/**
 * Formats a given price object to a currency string.
 *
 * @param price - The price object containing the amount in cents and the currency code.
 * @param negative - Indicates if the amount should be formatted as negative. Defaults to false.
 * @param rounded - Indicates if the amount should be rounded. Defaults to false.
 * @param dash - Indicates if .00 should be replaced with a dash. Defaults to false.
 * @returns The formatted currency string.
 */
export function formatPrice(price: Price | undefined, negative: boolean = false, rounded = false, dash = false): string {
    return formatCents(price?.amount, price?.currency, negative, rounded, dash);
}

/**
 * Returns the price as an absolute value (positive).
 *
 * @param price - The price object containing the amount in cents and the currency code.
 * @returns Positive price
 */
export function absPrice(price: Price | undefined): Price {
    return price ? { ...price, amount: Math.abs(price?.amount ?? 0) } : { amount: 0, currency: 'CHF' };
}

/**
 *
 * @param prices A list of prices to sum
 * @returns The sum of all prices as one Price object
 */
export function sumPrices(prices: Price[]): Price {
    return prices.reduce((acc, curr) => {
        return {
            amount: acc.amount + curr.amount,
            currency: acc.currency,
            vats: acc.vats
                ? acc.vats.map((vat, index) => ({
                        ...vat,
                        amount: vat.amount + (curr.vats ? curr.vats[index]?.amount || 0 : 0),
                    }))
                : curr.vats,
        };
    }, { amount: 0, currency: 'CHF', vats: [] });
}
